import { useCallback, useEffect, useState, type ReactNode } from "react";

import type { ManualCandidate, ManualAssignClient } from "../manual-assign.ts";

/**
 * `Manual assign` — the dispatcher's own path to a courier.
 *
 * ## What the design draws
 *
 * `19 WP18 Dispatch Operations.dc.html` draws no manual-assignment *page*. Its `FORMS.queue` entry
 * is a modal on the demand queue, and the tuple reads in order: the toolbar button says
 * **Manual assign**, the dialog is titled **Assign a driver manually** under the glyph
 * `person_pin_circle`, its subtitle is *"The driver still has to accept. Manual assigns are
 * recorded."*, it holds one fieldset legended **Assign** with three required fields, and the
 * primary button says **Send offer**.
 *
 * All three fields are required in the design (`1` in each field's last position), and the driver
 * field's hint is *"Sorted by distance; stale drivers are not listed"*.
 *
 * ## What it is actually doing
 *
 * Sending an offer, not making an assignment, which is what the subtitle says and what the
 * platform allows: `POST /v1/ride-assignments` requires an offer that already exists, so an
 * operator cannot bind a courier out of nothing and was never meant to.
 *
 * The driver list is DSP-01's ranking, asked for through DSP-04 — the console holds neither the
 * credentials to enumerate the fleet nor the rules about which products a courier's vehicle class
 * may serve, and should hold neither. The hint is therefore literally true rather than decorative:
 * a stale courier is absent because DSP-01's freshness policy dropped them, not because this screen
 * filtered a list.
 */

export interface ManualAssignProps {
  readonly client: ManualAssignClient;
  readonly tenantId: string;
  /** The demand the operator had selected. The design's `Request` field shows it. */
  readonly demandId: string | undefined;
  readonly onClose: () => void;
  /** Called after an offer is sent, so the queue can re-read the row it changed. */
  readonly onSent: () => void;
}

type Phase =
  | { readonly kind: "ranking" }
  | { readonly kind: "ready"; readonly candidates: readonly ManualCandidate[] }
  | { readonly kind: "failed"; readonly message: string }
  | { readonly kind: "sending" }
  | { readonly kind: "sent"; readonly offerId: string };

/** The design's own wording for a distance, which it writes as `0.8 km`. */
function kilometres(metres: number): string {
  return `${(metres / 1000).toFixed(1)} km`;
}

export function ManualAssign({
  client,
  tenantId,
  demandId,
  onClose,
  onSent,
}: ManualAssignProps): ReactNode {
  const [phase, setPhase] = useState<Phase>({ kind: "ranking" });
  const [driverId, setDriverId] = useState("");
  const [reason, setReason] = useState("");
  const [tried, setTried] = useState(false);

  const rank = useCallback(() => {
    if (demandId === undefined) return;
    setPhase({ kind: "ranking" });
    client
      .rank({ tenantId, demandId })
      .then((answer) => {
        setPhase({ kind: "ready", candidates: answer.candidates });
      })
      .catch((error: unknown) => {
        setPhase({
          kind: "failed",
          message: error instanceof Error ? error.message : "That did not work.",
        });
      });
  }, [client, demandId, tenantId]);

  useEffect(rank, [rank]);

  /*
   * Which fields are incomplete, named rather than counted.
   *
   * The design marks all three required and draws an invalid ring on each. `Request` is shown
   * rather than typed — it is the row the operator selected — so it is incomplete only when the
   * form was somehow opened without one, which the queue prevents and this still states.
   */
  const problems: string[] = [];
  if (demandId === undefined) problems.push("Request");
  if (driverId === "") problems.push("Driver");
  if (reason.trim() === "") problems.push("Reason");

  const candidates = phase.kind === "ready" ? phase.candidates : [];
  const busy = phase.kind === "ranking" || phase.kind === "sending";

  const send = (): void => {
    setTried(true);
    if (problems.length > 0 || demandId === undefined) return;
    setPhase({ kind: "sending" });
    client
      .sendOffer({ tenantId, demandId, driverId, reason: reason.trim() })
      .then((answer) => {
        setPhase({ kind: "sent", offerId: answer.offerId });
        onSent();
      })
      .catch((error: unknown) => {
        setPhase({
          kind: "failed",
          message: error instanceof Error ? error.message : "That did not work.",
        });
      });
  };

  return (
    <div
      className="queue__overlay"
      role="presentation"
      onClick={onClose}
      onKeyDown={(event) => {
        if (event.key === "Escape") onClose();
      }}
    >
      <div
        className="queue__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="manual-assign-title"
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        <div className="queue__dialog-head">
          <span aria-hidden="true" className="queue__dialog-glyph">
            person_pin_circle
          </span>
          <span className="queue__dialog-title">
            <strong id="manual-assign-title">Assign a driver manually</strong>
            <span className="queue__dialog-sub">
              The driver still has to accept. Manual assigns are recorded.
            </span>
          </span>
          <button
            type="button"
            className="queue__dialog-close"
            onClick={onClose}
            aria-label="Close"
          >
            <span aria-hidden="true">close</span>
          </button>
        </div>

        <div className="queue__dialog-body">
          {phase.kind === "sent" ? (
            <div role="status" className="queue__dialog-done">
              <span aria-hidden="true" className="queue__dialog-done-glyph">
                check_circle
              </span>
              <span>
                Offer sent. The driver still has to accept it — watch it on <strong>Offers</strong>.
              </span>
            </div>
          ) : null}

          {phase.kind === "failed" ? (
            <div role="alert" className="queue__dialog-error">
              <span aria-hidden="true" className="queue__dialog-error-glyph">
                error
              </span>
              <span>{phase.message}</span>
            </div>
          ) : null}

          {tried && problems.length > 0 && phase.kind !== "sent" ? (
            <div role="alert" className="queue__dialog-error">
              <span aria-hidden="true" className="queue__dialog-error-glyph">
                error
              </span>
              <span>
                Complete {problems.length} field{problems.length > 1 ? "s" : ""}:{" "}
                {problems.join(", ")}.
              </span>
            </div>
          ) : null}

          <fieldset className="queue__fieldset">
            <legend>Assign</legend>
            <div className="queue__field-grid">
              <label className="queue__field queue__field--wide">
                <span className="queue__label">
                  Request<span className="queue__required">*</span>
                </span>
                {/*
                  Read-only, and that is the one departure from the design's markup worth stating.
                  The design draws a text input placeholdered `e.g. req_8F44`, which predates the
                  form living on the queue: the ranking needs a demand's own pickup, product and
                  version, so the form has to arrive holding a row rather than a typed identifier.
                  DSP-04 publishes no listing to search one by, which Q-259 records. The field, its
                  label and its required marker are all still here.
                */}
                <input
                  type="text"
                  value={demandId ?? ""}
                  readOnly
                  placeholder="e.g. req_8F44"
                  aria-invalid={tried && demandId === undefined}
                />
              </label>

              <label className="queue__field queue__field--wide">
                <span className="queue__label">
                  Driver<span className="queue__required">*</span>
                </span>
                <select
                  value={driverId}
                  disabled={busy || phase.kind === "sent"}
                  aria-invalid={tried && driverId === ""}
                  onChange={(event) => {
                    setDriverId(event.target.value);
                  }}
                >
                  <option value="">
                    {phase.kind === "ranking"
                      ? "Finding drivers…"
                      : candidates.length === 0
                        ? "No driver is free for this ride"
                        : "Choose a driver"}
                  </option>
                  {candidates.map((candidate) => (
                    <option key={candidate.driverId} value={candidate.driverId}>
                      {/*
                        The design writes an option as `P. Njoroge · 0.8 km` — a person and a
                        distance. DSP-01 ranks by identifier and returns no name, so the name comes
                        from DRV-01's roster, and a courier it has no profile for shows the
                        identifier rather than being hidden from a dispatcher's only list.
                      */}
                      {candidate.displayName ?? candidate.driverId} ·{" "}
                      {kilometres(candidate.distanceMeters)}
                    </option>
                  ))}
                </select>
                <span className="queue__hint">
                  Sorted by distance; stale drivers are not listed
                </span>
              </label>

              <label className="queue__field queue__field--wide">
                <span className="queue__label">
                  Reason<span className="queue__required">*</span>
                </span>
                <textarea
                  rows={3}
                  value={reason}
                  disabled={phase.kind === "sent"}
                  aria-invalid={tried && reason.trim() === ""}
                  onChange={(event) => {
                    setReason(event.target.value);
                  }}
                />
              </label>
            </div>
          </fieldset>
        </div>

        <div className="queue__dialog-foot">
          <button type="button" className="queue__dialog-cancel" onClick={onClose}>
            {phase.kind === "sent" ? "Close" : "Cancel"}
          </button>
          {phase.kind === "sent" ? null : (
            <button type="button" className="queue__dialog-submit" disabled={busy} onClick={send}>
              {phase.kind === "sending" ? "Sending…" : "Send offer"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
